import { resolveApiBase } from './api'

// =============================================================================
// Types
// =============================================================================

export interface FeedbackAuthor {
  name: string | null
  email: string | null
}

export interface FeedbackItemTheme {
  id: number
  name: string
}

export interface FeedbackItem {
  id: number
  user_id: number
  title: string
  description: string
  type: string | null // 'bug' | 'feature' | 'improvement' | null
  status: string // 'backlog' | 'in_progress' | 'done' | 'dismissed'
  previous_status: string | null
  is_anonymous: boolean
  vote_count: number
  comment_count: number
  author: FeedbackAuthor
  // Legacy flat fields (kept for backward compat)
  author_name?: string | null
  author_email?: string | null
  has_voted: boolean
  merged_into_id: number | null
  themes?: FeedbackItemTheme[]
  created_at: string
  updated_at: string
}

export interface FeedbackListResponse {
  items: FeedbackItem[]
  total: number
  page: number
  per_page: number
}

export interface FeedbackImage {
  id: number
  filename: string | null
  content_type: string
  file_size: number
  created_at: string
}

export interface FeedbackComment {
  id: number
  user_id: number
  author: FeedbackAuthor
  // Legacy flat fields
  author_name?: string
  author_email?: string
  body: string
  parent_id: number | null
  replies: FeedbackComment[]
  created_at: string
}

export interface FeedbackDetail extends FeedbackItem {
  images: FeedbackImage[]
  comments: FeedbackComment[]
}

export interface FeedbackTheme {
  id: number
  name: string
  description: string | null
  item_count: number
  total_votes: number
  created_at: string
  updated_at: string
}

export interface FeedbackThemeDetail extends Omit<FeedbackTheme, 'item_count' | 'total_votes'> {
  items: FeedbackItem[]
}

export interface InternalNote {
  id: number
  admin_user_id: number
  admin_name: string
  body: string
  created_at: string
}

// =============================================================================
// Helpers
// =============================================================================

function getBase(): string {
  return resolveApiBase(window.location.pathname)
}

async function feedbackFetch<T>(
  path: string,
  options: RequestInit = {}
): Promise<T> {
  const base = getBase()
  const url = `${base}/feedback${path}`
  const headers: Record<string, string> = {
    ...(options.headers as Record<string, string>),
  }
  // Only set Content-Type for non-FormData bodies
  if (options.body && !(options.body instanceof FormData)) {
    headers['Content-Type'] = 'application/json'
  }

  const res = await fetch(url, {
    ...options,
    headers,
    credentials: 'include',
  })

  if (!res.ok) {
    const text = await res.text().catch(() => '')
    const error = new Error(text || `Request failed: ${res.status}`)
    ;(error as unknown as Record<string, unknown>).status = res.status
    throw error
  }

  // Handle 204 No Content
  if (res.status === 204) {
    return undefined as unknown as T
  }

  return res.json()
}

// =============================================================================
// API Functions
// =============================================================================

export async function createFeedback(data: {
  title: string
  description?: string
  type?: string | null
  is_anonymous?: boolean
}): Promise<FeedbackItem> {
  return feedbackFetch<FeedbackItem>('', {
    method: 'POST',
    body: JSON.stringify(data),
  })
}

export interface ListFeedbackParams {
  type?: string | null
  status?: string | null
  mine?: boolean
  search?: string
  sort?: string
  page?: number
  per_page?: number
  include_archived?: boolean
}

export async function listFeedback(
  params: ListFeedbackParams = {}
): Promise<FeedbackListResponse> {
  const query = new URLSearchParams()
  if (params.type) query.set('type', params.type)
  if (params.status) query.set('status', params.status)
  if (params.mine) query.set('mine', 'true')
  if (params.search) query.set('search', params.search)
  if (params.sort) query.set('sort', params.sort)
  if (params.page) query.set('page', String(params.page))
  if (params.per_page) query.set('per_page', String(params.per_page))
  if (params.include_archived) query.set('include_archived', 'true')

  const qs = query.toString()
  return feedbackFetch<FeedbackListResponse>(`${qs ? `?${qs}` : ''}`)
}

export async function getFeedbackDetail(
  id: number
): Promise<FeedbackDetail> {
  return feedbackFetch<FeedbackDetail>(`/${id}`)
}

export async function updateFeedback(
  id: number,
  data: { title?: string; description?: string; type?: string | null }
): Promise<FeedbackItem> {
  return feedbackFetch<FeedbackItem>(`/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  })
}

export async function uploadFeedbackImage(
  feedbackId: number,
  file: File
): Promise<FeedbackImage> {
  const formData = new FormData()
  formData.append('file', file)
  return feedbackFetch<FeedbackImage>(`/${feedbackId}/images`, {
    method: 'POST',
    body: formData,
  })
}

export async function deleteFeedbackImage(
  feedbackId: number,
  imageId: number
): Promise<void> {
  return feedbackFetch<void>(`/${feedbackId}/images/${imageId}`, {
    method: 'DELETE',
  })
}

export function getFeedbackImageUrl(imageId: number): string {
  const base = getBase()
  return `${base}/feedback/images/${imageId}`
}

export async function toggleVote(
  feedbackId: number
): Promise<{ has_voted: boolean; vote_count: number }> {
  return feedbackFetch<{ has_voted: boolean; vote_count: number }>(
    `/${feedbackId}/vote`,
    { method: 'POST' }
  )
}

export async function addComment(
  feedbackId: number,
  body: string,
  parentId?: number
): Promise<FeedbackComment> {
  return feedbackFetch<FeedbackComment>(`/${feedbackId}/comments`, {
    method: 'POST',
    body: JSON.stringify({ body, parent_id: parentId ?? null }),
  })
}

export async function deleteFeedback(
  feedbackId: number
): Promise<void> {
  return feedbackFetch<void>(`/${feedbackId}`, { method: 'DELETE' })
}

export async function reorderFeedback(
  orderedIds: number[]
): Promise<void> {
  return feedbackFetch<void>('/reorder', {
    method: 'POST',
    body: JSON.stringify({ ordered_ids: orderedIds }),
  })
}

export async function updateStatus(
  feedbackId: number,
  status: string
): Promise<FeedbackItem> {
  return feedbackFetch<FeedbackItem>(`/${feedbackId}/status`, {
    method: 'PUT',
    body: JSON.stringify({ status }),
  })
}

export async function mergeFeedback(
  targetId: number,
  sourceIds: number[]
): Promise<FeedbackItem> {
  return feedbackFetch<FeedbackItem>(`/${targetId}/merge`, {
    method: 'POST',
    body: JSON.stringify({ source_ids: sourceIds }),
  })
}

export async function addInternalNote(
  feedbackId: number,
  body: string
): Promise<InternalNote> {
  return feedbackFetch<InternalNote>(`/${feedbackId}/notes`, {
    method: 'POST',
    body: JSON.stringify({ body }),
  })
}

export async function getInternalNotes(
  feedbackId: number
): Promise<InternalNote[]> {
  return feedbackFetch<InternalNote[]>(`/${feedbackId}/notes`)
}

// =============================================================================
// Theme API Functions
// =============================================================================

export async function createTheme(data: {
  name: string
  description?: string | null
  feedback_ids?: number[]
}): Promise<FeedbackTheme> {
  return feedbackFetch<FeedbackTheme>('/themes', {
    method: 'POST',
    body: JSON.stringify(data),
  })
}

export async function listThemes(): Promise<FeedbackTheme[]> {
  return feedbackFetch<FeedbackTheme[]>('/themes')
}

export async function getThemeDetail(
  themeId: number
): Promise<FeedbackThemeDetail> {
  return feedbackFetch<FeedbackThemeDetail>(`/themes/${themeId}`)
}

export async function updateTheme(
  themeId: number,
  data: { name?: string; description?: string | null }
): Promise<FeedbackTheme> {
  return feedbackFetch<FeedbackTheme>(`/themes/${themeId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  })
}

export async function deleteTheme(themeId: number): Promise<void> {
  return feedbackFetch<void>(`/themes/${themeId}`, { method: 'DELETE' })
}

export async function addThemeItems(
  themeId: number,
  feedbackIds: number[]
): Promise<{ added: number; theme_id: number }> {
  return feedbackFetch<{ added: number; theme_id: number }>(
    `/themes/${themeId}/items`,
    { method: 'POST', body: JSON.stringify({ feedback_ids: feedbackIds }) }
  )
}

export async function removeThemeItems(
  themeId: number,
  feedbackIds: number[]
): Promise<{ removed: number; theme_id: number }> {
  return feedbackFetch<{ removed: number; theme_id: number }>(
    `/themes/${themeId}/items`,
    { method: 'DELETE', body: JSON.stringify({ feedback_ids: feedbackIds }) }
  )
}

// =============================================================================
// AI Types
// =============================================================================

export interface AIPainPoint {
  title: string
  description: string
  severity: 'critical' | 'high' | 'medium' | 'low'
  feedback_ids: number[]
}

export interface AIFeatureRequest {
  title: string
  description: string
  vote_count: number
  feedback_ids: number[]
}

export interface AIDuplicate {
  group_title: string
  feedback_ids: number[]
  similarity_reason: string
}

export interface AIPriority {
  title: string
  rationale: string
  feedback_ids: number[]
  priority: 'critical' | 'high' | 'medium' | 'low'
}

export interface AIStats {
  by_type: Record<string, number>
  by_status: Record<string, number>
  total_items: number
  total_votes: number
  avg_votes_per_item: number
}

export interface AISummaryData {
  executive_brief: string
  pain_points: AIPainPoint[]
  feature_requests: AIFeatureRequest[]
  potential_duplicates: AIDuplicate[]
  suggested_priorities: AIPriority[]
  stats: AIStats
}

export interface AISummaryResponse {
  summary: AISummaryData
  generated_at: string
  item_count: number
  is_stale: boolean
  new_items_count: number
}

export interface AutoThemeProposal {
  name: string
  description: string
  feedback_ids: number[]
  rationale: string
}

export interface AutoThemeResponse {
  themes: AutoThemeProposal[]
  unclassifiable_ids: number[]
}

// =============================================================================
// AI API Functions
// =============================================================================

export async function getAISummary(): Promise<AISummaryResponse> {
  return feedbackFetch<AISummaryResponse>('/ai/summary')
}

export async function generateAISummary(): Promise<AISummaryResponse> {
  return feedbackFetch<AISummaryResponse>('/ai/summary', {
    method: 'POST',
  })
}

export async function generateAutoThemes(): Promise<AutoThemeResponse> {
  return feedbackFetch<AutoThemeResponse>('/ai/auto-theme', {
    method: 'POST',
  })
}
